import { Map, fromJS } from 'immutable';
import { normalize } from 'normalizr';

import { mapListToRecords, normalizeList } from '@sophocles/entities/entityUtils';
import { SiteGroup, siteGroupSchema } from '@sophocles/entities/SiteGroup/siteGroupRecord';
import { receiveSiteGroupWeatherConfigurations } from '@sophocles/entities/SiteGroupWeatherConfiguration';
import { addToast } from '@sophocles/features/toast';
import apiClient from '@sophocles/http-common';
import {
  checkHttpStatus,
  dataTableParamFormatter,
  generateQueryParams,
  genericErrorAction,
  getAppString,
  mapToQueryParams,
} from '@sophocles/utils';

/* Receive Actions */
export const RECEIVE_SITE_GROUPS = 'RECEIVE_SITE_GROUPS';
export function receiveSiteGroups(json) {
  return {
    type: RECEIVE_SITE_GROUPS,
    siteGroups: mapListToRecords(json, SiteGroup),
  };
}

/* Request Actions */
export const REQUEST_CUSTOMER_SITE_GROUPS = 'REQUEST_CUSTOMER_SITE_GROUPS';
function requestCustomerSiteGroups(customerId) {
  return {
    type: REQUEST_CUSTOMER_SITE_GROUPS,
    customerId,
  };
}

export const REQUEST_SITE_GROUP = 'REQUEST_SITE_GROUP';
function requestSiteGroups(sitegroupId) {
  return {
    type: REQUEST_SITE_GROUP,
    sitegroupId,
  };
}

/* REMOVE ACTION */
export const REMOVE_SITE_GROUPS = 'REMOVE_SITE_GROUPS';
export function removeSiteGroups() {
  return {
    type: REMOVE_SITE_GROUPS,
  };
}

/* Fetch Actions */
export function fetchSiteGroupsForCustomer(customerId) {
  return dispatch => {
    dispatch(requestCustomerSiteGroups(customerId));
    return apiClient
      .get(`/customers/${customerId}/sitegroups/`, { params: { limit: 10000 } })
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data.results, 'siteGroups', siteGroupSchema))
      .then(json => dispatch(receiveSiteGroups(json.entities.siteGroups)));
  };
}

export function fetchSiteGroupListForCustomer(customerId, isVirtual = null) {
  return dispatch => {
    dispatch(requestCustomerSiteGroups(customerId));
    return apiClient
      .get(`/customers/${customerId}/sitegroups/minimal/`, { params: { is_virtual: isVirtual, limit: 10000 } })
      .then(checkHttpStatus)
      .then(response => fromJS(response.data.results));
  };
}

export function fetchCustomerSiteGroupListWithFilters(
  offset,
  limit,
  customerId,
  filterObject = {},
  search,
  searchFields = [],
) {
  return dispatch => {
    const params = { ...(filterObject ?? {}), offset, limit, customer: customerId };
    if (search) {
      params.text_search = searchFields.join(',') + '|' + search;
    }
    return apiClient
      .get(`/customers/${customerId}/sitegroups/minimal/`, { params })
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, 'REQUEST_SITE_GROUPS_FOR_LIST')));
  };
}

/* Fetch Actions */
export function fetchSiteGroup(sitegroupId) {
  return dispatch => {
    dispatch(requestSiteGroups(sitegroupId));
    return apiClient
      .get(`/sitegroups/${sitegroupId}/`)
      .then(checkHttpStatus)
      .then(response => {
        return normalize(response.data, siteGroupSchema);
      })
      .then(json => {
        dispatch(receiveSiteGroupWeatherConfigurations(json.entities.siteGroupWeatherConfigurations));
        return dispatch(receiveSiteGroups(json.entities.siteGroups));
      })
      .catch(error => {
        return dispatch(genericErrorAction(error, REQUEST_SITE_GROUP));
      });
  };
}

export function fetchRawSiteGroup(sitegroupId) {
  return dispatch => {
    dispatch(requestSiteGroups(sitegroupId));
    return apiClient
      .get(`/sitegroups/${sitegroupId}/`)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_SITE_GROUP)));
  };
}

export function fetchRawSiteGroups(filterParams) {
  return dispatch => {
    const params = generateQueryParams(filterParams);
    return apiClient
      .get('/sitegroups/', { params })
      .then(checkHttpStatus)
      .then(response => response.data);
  };
}

/* Edit Actions */
export const EDIT_SITE_GROUP = 'EDIT_SITE_GROUP';
export function editSiteGroup(site) {
  return {
    type: EDIT_SITE_GROUP,
    site,
  };
}

export function patchSiteGroup(sitegroupId, values, isBillboard) {
  return dispatch => {
    dispatch(editSiteGroup(sitegroupId));
    return apiClient
      .patch(`/sitegroups/${sitegroupId}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteGroupSchema))
      .then(json => {
        dispatch(
          addToast({
            title: 'Success',
            message: `${isBillboard ? 'Plant' : 'Site Group'} updated!`,
            level: 'success',
            dismissible: true,
          }),
        );
        return dispatch(receiveSiteGroups(json.entities.siteGroups));
      })
      .catch(error => dispatch(genericErrorAction(error, EDIT_SITE_GROUP)));
  };
}

/* Create Actions */
export const CREATE_SITE_GROUP = 'CREATE_SITE_GROUP';
function sayCreateSiteGroup(values) {
  return {
    type: CREATE_SITE_GROUP,
    values: values,
  };
}

export function createSiteGroup(values) {
  return dispatch => {
    dispatch(sayCreateSiteGroup(values));
    return apiClient
      .post(`/sitegroups/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, siteGroupSchema))
      .then(json => {
        const applicationId = json.entities.siteGroups[json.result].application;
        dispatch(
          addToast({
            title: 'Success',
            message: `${getAppString('siteGroup', applicationId)} created!`,
            level: 'success',
            dismissible: true,
          }),
        );
        return dispatch(receiveSiteGroups(json.entities.siteGroups));
      })
      .catch(error => dispatch(genericErrorAction(error, CREATE_SITE_GROUP)));
  };
}

export function fetchSiteListingForSiteGroup(sitegroupId) {
  return dispatch => {
    dispatch(requestSiteGroups(sitegroupId));
    return apiClient
      .get(`/sitegroups/${sitegroupId}/xlsx/`, { responseType: 'blob' })
      .then(checkHttpStatus)
      .then(response => response.data)
      .catch(error => dispatch(genericErrorAction(error, REQUEST_SITE_GROUP)));
  };
}

export function patchSiteGroupOptions(optionsId, values) {
  return dispatch =>
    apiClient
      .patch(`/sitegroupoptions/${optionsId}/`, values)
      .then(checkHttpStatus)
      .then(response => {
        // const applicationId = json.entities.siteGroups[json.result].application;
        dispatch(
          addToast({
            title: 'Success',
            message: 'Options updated',
            level: 'success',
            dismissible: true,
          }),
        );
        return response.data;
      })
      .catch(error => dispatch(genericErrorAction(error, 'PATCH_SITE_GROUP_OPTIONS')));
}

export function fetchSiteGroupsWithFilters(filterObject) {
  return tableParams => dispatch => {
    const params = Map(filterObject).merge(dataTableParamFormatter(tableParams));
    return (
      apiClient
        .get(`/sitegroups/?${mapToQueryParams(params)}`)
        .then(checkHttpStatus)
        .then(response => fromJS(response.data))
        // .then( json => dispatch(receiveLimitedUsers(json.entities.users)))
        .catch(error => dispatch(genericErrorAction(error, 'REQUEST_FILTERED_SITE_GROUPS')))
    );
  };
}
